import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X, Music2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0a0a0a]/95 backdrop-blur-md shadow-lg shadow-[#D4AF37]/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => scrollToSection('hero')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/8zqnwov9_1AC2C9EE-62B2-429B-AE9A-CFA74123D7DA%20%281%29.jpg"
              alt="40 Songs and a Mic"
              className="h-12 w-auto"
            />
            <div className="hidden sm:block">
              <div className="text-white font-bold text-xl leading-tight" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
                40 SONGS AND A MIC
              </div>
              <div className="text-[#D4AF37] text-xs font-semibold leading-tight">
                Gospel & Hip-Hop Healing
              </div>
            </div>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <button onClick={() => scrollToSection('workbooks')} className="text-gray-300 hover:text-[#D4AF37] font-medium transition-colors duration-200">
              Workbooks
            </button>
            <button onClick={() => scrollToSection('merch-store')} className="text-gray-300 hover:text-[#D4AF37] font-medium transition-colors duration-200">
              Merch
            </button>
            <button onClick={() => scrollToSection('about-media')} className="text-gray-300 hover:text-[#D4AF37] font-medium transition-colors duration-200">
              About
            </button>
            <button onClick={() => scrollToSection('podcast')} className="text-gray-300 hover:text-[#D4AF37] font-medium transition-colors duration-200">
              Podcast
            </button>
            <button onClick={() => scrollToSection('music')} className="text-gray-300 hover:text-[#D4AF37] font-medium transition-colors duration-200">
              Music
            </button>
            <Link to="/poetry" data-testid="nav-poetry" className="text-gray-300 hover:text-[#D4AF37] font-medium transition-colors duration-200">
              Poetry
            </Link>
            <button onClick={() => scrollToSection('sponsor')} className="text-gray-300 hover:text-[#D4AF37] font-medium transition-colors duration-200">
              Sponsor
            </button>
            <Button
              onClick={() => scrollToSection('workbooks')}
              data-testid="nav-shop-workbooks"
              className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a] hover:from-[#FFD700] hover:to-[#D4AF37] font-bold px-6 py-2 transition-all duration-300 transform hover:scale-105"
            >
              SHOP WORKBOOKS
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="nav-hamburger"
            className="md:hidden text-white p-2"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-[#0a0a0a] border-t border-[#D4AF37]/20">
          <div className="px-4 py-4 space-y-3">
            <button onClick={() => scrollToSection('workbooks')} className="block w-full text-left text-gray-300 hover:text-[#D4AF37] font-medium py-2 transition-colors duration-200">
              Workbooks
            </button>
            <button onClick={() => scrollToSection('merch-store')} className="block w-full text-left text-gray-300 hover:text-[#D4AF37] font-medium py-2 transition-colors duration-200">
              Merch
            </button>
            <button onClick={() => scrollToSection('about-media')} className="block w-full text-left text-gray-300 hover:text-[#D4AF37] font-medium py-2 transition-colors duration-200">
              About
            </button>
            <button onClick={() => scrollToSection('podcast')} className="block w-full text-left text-gray-300 hover:text-[#D4AF37] font-medium py-2 transition-colors duration-200">
              Podcast
            </button>
            <button onClick={() => scrollToSection('music')} className="block w-full text-left text-gray-300 hover:text-[#D4AF37] font-medium py-2 transition-colors duration-200">
              Music
            </button>
            <Link to="/poetry" onClick={() => setIsMobileMenuOpen(false)} data-testid="nav-poetry-mobile" className="block w-full text-left text-gray-300 hover:text-[#D4AF37] font-medium py-2 transition-colors duration-200">
              Poetry
            </Link>
            <button onClick={() => scrollToSection('sponsor')} className="block w-full text-left text-gray-300 hover:text-[#D4AF37] font-medium py-2 transition-colors duration-200">
              Sponsor
            </button>
            <Button
              onClick={() => scrollToSection('workbooks')}
              data-testid="nav-shop-workbooks-mobile"
              className="w-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a] hover:from-[#FFD700] hover:to-[#D4AF37] font-bold py-3 transition-all duration-300"
            >
              SHOP WORKBOOKS
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
